import React, { useEffect, useRef, useState } from "react";
import { Hash, Bell, Mail, ListChecks, CalendarClock } from "lucide-react";
import { tasksApi, TaskCodeSettings as TaskCodeSettingsT } from "../../api/tasks";
import { useToast } from "../../hooks/useToast";

/** Refined pill switch built on the Teamlio (--gt-*) design tokens. */
function Toggle({
  checked,
  onChange,
  disabled,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      data-on={checked ? "true" : "false"}
      onClick={() => onChange(!checked)}
      className="tset-switch"
    >
      <span className="tset-knob" />
    </button>
  );
}

export default function TaskCodeSettings() {
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<TaskCodeSettingsT | null>(null);

  // Local form state — mirrors `settings` while editing.
  const [prefix, setPrefix] = useState("TASK-");
  const [suffix, setSuffix] = useState("");
  // Padding is fixed at 1 — the field is no longer exposed in the UI,
  // so generated codes look like "TASK-1", "TASK-2" instead of zero-
  // padded "TASK-0001". Kept as a constant rather than a state to make
  // the preview / save plumbing one-line change.
  const padding = 1;
  const [startNumber, setStartNumber] = useState<number>(1);
  const [startNumberInput, setStartNumberInput] = useState<string>("1");

  // ── Email notification toggles (per-branch, admin). Default ON to match
  //    the backend's "no row == all on" behavior until the API says otherwise.
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [emailOnTask, setEmailOnTask] = useState(true);
  const [emailOnPlanner, setEmailOnPlanner] = useState(true);

  // Baselines captured at load / after each save — let the single Save send
  // ONLY what actually changed. Critical for the ID series: the backend resets
  // the live counter to start_number whenever start_number is sent, so we must
  // not re-save the series just because an email toggle flipped.
  const codeBase = useRef({ prefix: "TASK-", suffix: "", start: 1 });
  const notifBase = useRef({ enabled: true, task: true, planner: true });

  // Initial load — task code series.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await tasksApi.getCodeSettings();
        const s: TaskCodeSettingsT | null = r.data?.settings || null;
        if (cancelled) return;
        if (s) {
          setSettings(s);
          setPrefix(s.prefix ?? "TASK-");
          setSuffix(s.suffix ?? "");
          // Padding intentionally ignored — UI no longer exposes it.
          // Save will always send 1, normalising any existing setting.
          const initial = s.start_number ?? 1;
          setStartNumber(initial);
          setStartNumberInput(String(initial));
          codeBase.current = { prefix: s.prefix ?? "TASK-", suffix: s.suffix ?? "", start: initial };
        }
      } catch (e: any) {
        toast({
          variant: "destructive",
          description: e?.response?.data?.detail || "Failed to load task code settings",
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initial load — email notification toggles (null == all on).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await tasksApi.getNotificationSettings();
        const n = r.data?.settings || null;
        if (cancelled || !n) return;
        setEmailEnabled(!!n.email_enabled);
        setEmailOnTask(!!n.email_on_task);
        setEmailOnPlanner(!!n.email_on_planner);
        notifBase.current = {
          enabled: !!n.email_enabled,
          task: !!n.email_on_task,
          planner: !!n.email_on_planner,
        };
      } catch {
        // Non-fatal: leave the switches at their ON defaults.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Live preview using the *current* form values, not the server values.
  // We match the backend's f"{prefix}{n:0{width}d}{suffix}" format.
  const previewNumber = settings && startNumber === settings.start_number
    ? settings.next_number
    : startNumber;
  const previewPadded = String(previewNumber).padStart(Math.max(1, padding), "0");
  const preview = `${prefix}${previewPadded}${suffix}`;

  // Single Save — persists the ID series and the email toggles in one click,
  // sending ONLY what changed. start_number is included only when the admin
  // actually edited it, so the live counter is never reset by an email-only
  // (or prefix/suffix-only) save.
  const handleSaveAll = async () => {
    const codePayload: {
      prefix?: string; suffix?: string; padding?: number; start_number?: number;
    } = {};
    if (prefix !== codeBase.current.prefix) codePayload.prefix = prefix;
    if (suffix !== codeBase.current.suffix) codePayload.suffix = suffix;
    if (startNumber !== codeBase.current.start) {
      if (startNumber < 1) {
        toast({ variant: "destructive", description: "Start number must be at least 1." });
        return;
      }
      codePayload.start_number = startNumber;
      codePayload.padding = padding;
    }
    const codeChanged = Object.keys(codePayload).length > 0;
    const notifChanged =
      emailEnabled !== notifBase.current.enabled ||
      emailOnTask !== notifBase.current.task ||
      emailOnPlanner !== notifBase.current.planner;

    if (!codeChanged && !notifChanged) {
      toast({ description: "Nothing to save — no changes yet." });
      return;
    }

    setSaving(true);
    try {
      const jobs: Promise<any>[] = [];
      if (codeChanged) {
        jobs.push(
          tasksApi.saveCodeSettings(codePayload).then((r) => {
            const s: TaskCodeSettingsT = r.data?.settings;
            if (s) {
              setSettings(s);
              codeBase.current = {
                prefix: s.prefix ?? "TASK-",
                suffix: s.suffix ?? "",
                start: s.start_number ?? 1,
              };
            }
          })
        );
      }
      if (notifChanged) {
        jobs.push(
          tasksApi
            .saveNotificationSettings({
              email_enabled: emailEnabled,
              email_on_task: emailOnTask,
              email_on_planner: emailOnPlanner,
            })
            .then(() => {
              notifBase.current = { enabled: emailEnabled, task: emailOnTask, planner: emailOnPlanner };
            })
        );
      }
      await Promise.all(jobs);
      toast({ description: "Settings saved." });
    } catch (e: any) {
      toast({
        variant: "destructive",
        description: e?.response?.data?.detail || "Failed to save settings",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="tset-scope">
        <Style />
        <div className="tset-wrap">
          <div className="tset-meta" style={{ padding: 28 }}>Loading task settings…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="tset-scope">
      <Style />
      <div className="tset-wrap">
        {/* ───────────────────────────── Page header ───────────────────── */}
        <header className="tset-rise" style={{ animationDelay: "0ms" }}>
          <div className="tset-eyebrow">Workspace · Tasks</div>
          <h1 className="tset-display">Task Settings</h1>
          <p className="tset-sub">
            Configure the readable ID series and decide which task emails this
            branch sends — all in one place.
          </p>
        </header>

        <div className="tset-cols">
        {/* ───────────────────────────── Task ID Series ────────────────── */}
        <section className="tset-card tset-rise" style={{ animationDelay: "90ms" }}>
          <div className="tset-chead">
            <span className="tset-badge"><Hash size={16} /></span>
            <div>
              <div className="tset-eyebrow">Series</div>
              <h2 className="tset-ctitle">Task ID Series</h2>
              <p className="tset-cdesc">
                The series that auto-generates every new task's readable ID. It's
                read-only on the Create Task form — only the values below change it.
              </p>
            </div>
          </div>

          {/* Specimen — the next code, treated as the hero of this card */}
          <div className="tset-hero">
            <div className="tset-hero-eyebrow">Next Task ID</div>
            <div className="tset-code">{preview}</div>
          </div>

          <div className="tset-fields">
            <div>
              <label className="tset-flabel">Prefix</label>
              <input
                type="text"
                value={prefix}
                onChange={(e) => setPrefix(e.target.value)}
                maxLength={20}
                placeholder="TASK-"
                className="tset-input"
              />
              <p className="tset-hint">Letters before the number, e.g. TASK-.</p>
            </div>

            <div>
              <label className="tset-flabel">Suffix</label>
              <input
                type="text"
                value={suffix}
                onChange={(e) => setSuffix(e.target.value)}
                maxLength={20}
                placeholder="(optional)"
                className="tset-input"
              />
              <p className="tset-hint">Optional, e.g. -2026.</p>
            </div>

            <div className="tset-field-start">
              <label className="tset-flabel">Start Number</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={startNumberInput}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, "");
                  const cleaned = digits.replace(/^0+(?=\d)/, "");
                  setStartNumberInput(cleaned);
                  setStartNumber(cleaned === "" ? 0 : parseInt(cleaned, 10));
                }}
                onFocus={(e) => e.target.select()}
                onBlur={() => {
                  if (startNumberInput === "") setStartNumberInput("0");
                }}
                className="tset-input"
              />
              <p className="tset-hint">
                Where the series starts. Existing tasks keep their codes.
              </p>
            </div>
          </div>

          {settings && (
            <p className="tset-cardmeta">
              Counter at <b>{settings.next_number}</b>
              {settings.updated_at &&
                ` · updated ${new Date(settings.updated_at).toLocaleDateString()}`}
            </p>
          )}
        </section>

        {/* ─────────────────────────── Email Notifications ─────────────── */}
        <section className="tset-card tset-rise" style={{ animationDelay: "170ms" }}>
          <div className="tset-chead">
            <span className="tset-badge"><Bell size={16} /></span>
            <div>
              <div className="tset-eyebrow">Delivery</div>
              <h2 className="tset-ctitle">Email Notifications</h2>
              <p className="tset-cdesc">
                Choose which task emails this branch sends. The in-app notification
                bell and push notifications are never affected by these settings.
              </p>
            </div>
          </div>

          {/* Master switch — soft green container signals it governs the rest */}
          <div className="tset-master">
            <span className="tset-ico tset-ico-on"><Mail size={18} /></span>
            <div className="tset-rgrow">
              <p className="tset-rtitle">Email notifications</p>
              <p className="tset-rdesc">
                Master switch. Turn off to stop all task &amp; planner emails for this branch.
              </p>
            </div>
            <Toggle checked={emailEnabled} onChange={setEmailEnabled} label="Email notifications" />
          </div>

          <div className="tset-eyebrow tset-group-label">Send emails for</div>
          <div className="tset-group" style={{ opacity: emailEnabled ? 1 : 0.5 }}>
            <div className="tset-row">
              <span className="tset-ico"><ListChecks size={17} /></span>
              <div className="tset-rgrow">
                <p className={`tset-rtitle ${emailEnabled ? "" : "is-off"}`}>Task notifications</p>
                <p className="tset-rdesc">When a task is assigned to someone or its status changes.</p>
              </div>
              <Toggle
                checked={emailEnabled && emailOnTask}
                onChange={setEmailOnTask}
                disabled={!emailEnabled}
                label="Task notifications"
              />
            </div>

            <div className="tset-row">
              <span className="tset-ico"><CalendarClock size={17} /></span>
              <div className="tset-rgrow">
                <p className={`tset-rtitle ${emailEnabled ? "" : "is-off"}`}>Daily Planner notifications</p>
                <p className="tset-rdesc">Digest emails when teammates assign to-dos to someone's planner.</p>
              </div>
              <Toggle
                checked={emailEnabled && emailOnPlanner}
                onChange={setEmailOnPlanner}
                disabled={!emailEnabled}
                label="Daily Planner notifications"
              />
            </div>
          </div>

        </section>
        </div>

        {/* ─────────────── One Save for the whole page ─────────────────── */}
        <div className="tset-actionbar tset-rise" style={{ animationDelay: "240ms" }}>
          <p className="tset-meta">These settings apply to everyone in this branch.</p>
          <button onClick={handleSaveAll} disabled={saving} className="tset-btn tset-btn-lg">
            {saving ? "Saving…" : "Save Settings"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Scoped styles — consume the global Teamlio (--gt-*) tokens directly so the
 *  page is on-brand regardless of the .gt-themed utility remap. */
function Style() {
  return (
    <style>{`
      /* Compact: the whole page is designed to fit one viewport without scroll. */
      .tset-wrap { max-width: 74rem; margin: 0 auto; padding: 0 2px 14px;
        display: flex; flex-direction: column; gap: 12px; }

      /* Two columns — Task ID Series | Email Notifications, half/half */
      .tset-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; align-items: start; }
      @media (max-width: 920px) { .tset-cols { grid-template-columns: 1fr; } }

      @keyframes tset-rise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
      .tset-rise { animation: tset-rise var(--gt-t-slow) both; }

      /* Header */
      .tset-eyebrow { font-size: 10.5px; font-weight: 700; letter-spacing: var(--gt-letter-eyebrow);
        text-transform: uppercase; color: var(--gt-text-tertiary); }
      .tset-display { margin: 3px 0 4px; font-family: var(--gt-font-serif) !important; font-style: italic;
        font-weight: 500; font-size: clamp(24px, 2.6vw, 30px); line-height: 1.03;
        letter-spacing: -0.01em; color: var(--gt-primary); }
      .tset-sub { margin: 0; color: var(--gt-text-secondary); font-size: 13px; line-height: 1.45; max-width: 70ch; }

      /* Card */
      .tset-card { position: relative; background: var(--gt-surface-0); border: 1px solid var(--gt-border);
        border-radius: var(--gt-radius-card); box-shadow: var(--gt-elev-2); padding: 16px 18px; overflow: hidden; }
      .tset-card::before { content: ""; position: absolute; top: 0; left: 0; right: 0; height: 1px;
        background: linear-gradient(90deg, transparent, rgba(255,255,255,0.85), transparent); }

      .tset-chead { display: flex; align-items: flex-start; gap: 11px; margin-bottom: 12px; }
      .tset-badge { flex: none; width: 30px; height: 30px; border-radius: 9px; display: grid; place-items: center;
        background: var(--gt-primary-container); color: var(--gt-primary); margin-top: 1px; }
      .tset-ctitle { margin: 1px 0 3px; font-family: var(--gt-font-serif) !important; font-style: italic;
        font-weight: 600; font-size: 18px; line-height: 1.1; color: var(--gt-text-primary); }
      .tset-cdesc { margin: 0; font-size: 12px; line-height: 1.45; color: var(--gt-text-secondary); max-width: 58ch; }

      /* Specimen / hero */
      .tset-hero { position: relative; border-radius: 13px; padding: 11px 16px; margin-bottom: 13px;
        background: linear-gradient(135deg, var(--gt-grad-from), var(--gt-grad-to));
        box-shadow: var(--gt-glow); overflow: hidden; }
      .tset-hero::after { content: ""; position: absolute; inset: 0; pointer-events: none; opacity: 0.55;
        background-image: radial-gradient(rgba(255,255,255,0.12) 1px, transparent 1.4px); background-size: 13px 13px;
        -webkit-mask-image: linear-gradient(105deg, #000, transparent 70%); mask-image: linear-gradient(105deg, #000, transparent 70%); }
      .tset-hero-eyebrow { position: relative; font-size: 10px; font-weight: 700; letter-spacing: var(--gt-letter-eyebrow);
        text-transform: uppercase; color: var(--gt-primary-container); }
      .tset-code { position: relative; margin-top: 3px; font-family: var(--gt-font-mono) !important; font-weight: 500;
        font-size: clamp(21px, 2.6vw, 26px); letter-spacing: 0.02em; color: #ffffff; }

      /* Fields */
      .tset-fields { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 14px; }
      .tset-field-start { grid-column: 1 / 2; }
      @media (max-width: 640px) { .tset-fields { grid-template-columns: 1fr; } .tset-field-start { grid-column: 1; } }
      .tset-flabel { display: block; margin-bottom: 4px; font-family: var(--gt-font-serif) !important; font-style: italic;
        font-weight: 500; font-size: 13px; color: var(--gt-text-secondary); }
      .tset-input { width: 100%; box-sizing: border-box; font-family: var(--gt-font-mono) !important; font-size: 13.5px;
        padding: 8px 12px; border-radius: var(--gt-radius-input); background: var(--gt-surface-1);
        border: 1px solid var(--gt-border); color: var(--gt-text-primary);
        transition: border-color var(--gt-t-fast), box-shadow var(--gt-t-fast), background var(--gt-t-fast); }
      .tset-input::placeholder { color: var(--gt-text-disabled); }
      .tset-input:focus { outline: none; border-color: var(--gt-primary); background: var(--gt-surface-0);
        box-shadow: 0 0 0 3px rgba(15, 45, 31, 0.10); }
      .tset-hint { margin: 4px 0 0; font-size: 11px; line-height: 1.35; color: var(--gt-text-tertiary); }

      /* Card meta (counter line under the series fields) */
      .tset-cardmeta { margin: 12px 0 0; padding-top: 10px; border-top: 1px solid var(--gt-divider);
        font-size: 11.5px; color: var(--gt-text-tertiary); }
      .tset-cardmeta b { font-family: var(--gt-font-mono) !important; font-weight: 500; color: var(--gt-text-secondary); }

      /* Page-level action bar — one Save for the whole page, pinned to bottom */
      .tset-actionbar { position: sticky; bottom: 8px; z-index: 5;
        display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap;
        margin-top: 2px; padding: 11px 12px 11px 18px; background: var(--gt-surface-0);
        border: 1px solid var(--gt-border); border-radius: 14px; box-shadow: var(--gt-elev-4); }
      .tset-meta { margin: 0; font-size: 12px; color: var(--gt-text-tertiary); }
      .tset-meta b { font-family: var(--gt-font-mono) !important; font-weight: 500; color: var(--gt-text-secondary); }
      .tset-btn-lg { padding: 11px 24px; font-size: 14px; }

      /* Notification rows */
      .tset-master { display: flex; align-items: center; gap: 12px; padding: 11px 13px;
        border-radius: 13px; background: var(--gt-primary-container); border: 1px solid var(--gt-accent-soft); }
      .tset-group-label { margin: 11px 2px 6px; }
      .tset-group { border: 1px solid var(--gt-border); border-radius: 13px; padding: 0 14px;
        background: var(--gt-surface-0); transition: opacity var(--gt-t-mid); }
      .tset-row { display: flex; align-items: center; gap: 12px; padding: 11px 0; }
      .tset-row + .tset-row { border-top: 1px solid var(--gt-divider); }
      .tset-rgrow { flex: 1 1 auto; min-width: 0; }
      .tset-ico { flex: none; width: 34px; height: 34px; border-radius: 10px; display: grid; place-items: center;
        background: var(--gt-surface-2); color: var(--gt-primary-soft); }
      .tset-ico-on { background: var(--gt-surface-0); color: var(--gt-primary); border: 1px solid var(--gt-accent-soft); }
      .tset-rtitle { margin: 0; font-weight: 600; font-size: 14px; color: var(--gt-text-primary); }
      .tset-rtitle.is-off { color: var(--gt-text-tertiary); }
      .tset-rdesc { margin: 2px 0 0; font-size: 11.5px; line-height: 1.4; color: var(--gt-text-tertiary); }

      /* Switch */
      .tset-switch { position: relative; flex: none; width: 44px; height: 25px; border-radius: 9999px;
        cursor: pointer; padding: 0; border: 1px solid var(--gt-border-strong); background: var(--gt-surface-3);
        transition: background var(--gt-t-mid), border-color var(--gt-t-mid); }
      .tset-switch[data-on="true"] { background: var(--gt-primary); border-color: var(--gt-primary); }
      .tset-switch:disabled { opacity: 0.45; cursor: not-allowed; }
      .tset-switch:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(15, 45, 31, 0.18); }
      .tset-knob { position: absolute; top: 50%; left: 2px; width: 18px; height: 18px; border-radius: 9999px;
        background: #ffffff; box-shadow: var(--gt-elev-2); transform: translate(0, -50%);
        transition: transform var(--gt-t-mid); }
      .tset-switch[data-on="true"] .tset-knob { transform: translate(19px, -50%); }

      /* Primary button — force white text; the global .gt-themed button rule
         otherwise paints it dark (var(--gt-text-primary)) over the green bg. */
      .tset-btn { display: inline-flex; align-items: center; gap: 8px; font-weight: 600; font-size: 14px;
        padding: 10px 20px; border: none; border-radius: 12px; color: #ffffff !important; background: var(--gt-primary);
        cursor: pointer; box-shadow: var(--gt-glow-soft);
        transition: transform var(--gt-t-fast), background var(--gt-t-fast), box-shadow var(--gt-t-fast); }
      .tset-btn:hover:not(:disabled) { background: var(--gt-primary-hover); transform: translateY(-1px); box-shadow: var(--gt-glow); }
      .tset-btn:active:not(:disabled) { transform: translateY(0); }
      .tset-btn:disabled { opacity: 0.6; cursor: not-allowed; }
    `}</style>
  );
}
